import { z } from 'zod';
import { BaseSchema } from '../base/base.entity.model';
import * as FlatModels from '../../internal';

export const PageType = z.enum([
  'Folder',
  'Grid',
  'Case',
  'Slide',
  'Training',
  'QuizStart',
  'QuizRun',
  'Vocabulary',
  'InfoPage'
]);
export type PageType = z.infer<typeof PageType>;

export const PageHasDescription = (page: PageEntityType) => {
  return page.type === 'Folder' ||
         page.type === 'Grid' ||
         page.type === 'Slide' ||
         page.type === 'Training';
}

export const PageCanGeneratePdf = (page: PageEntityType) => {
  return page.type === 'Case' ||
         page.type === 'Grid' ||
         page.type === 'Slide' ||
         page.type === 'Training';
}

export const PageHasBody = (page: PageEntityType) => {
  return page.type === 'Folder' ||
         page.type === 'Grid' ||
         page.type === 'Slide' ||
         page.type === 'Training' ||
         page.type === 'QuizStart' ||
         page.type === 'Vocabulary';
}

// isBlock$
export const PageHasSlides = (page: PageEntityType) => {
  return page.type === 'Grid' ||
         page.type === 'Case' ||
         page.type === 'QuizRun';
}

export const PageTypeArray = [PageType.enum.Grid, PageType.enum.Case].map(value => { return { value: value, name: 'enum.pageType.' + value }; });

export const BlockPageTypeArray = [PageType.enum.Folder, PageType.enum.Vocabulary, PageType.enum.QuizStart].map(value => { return { value: value, name: 'enum.pageType.' + value }; });

export const AllowedSearchPageTypeArray = [PageType.enum.Folder, PageType.enum.Grid, PageType.enum.Case, PageType.enum.Slide, PageType.enum.Training, PageType.enum.QuizStart, PageType.enum.Vocabulary].reduce((arr, val) => {
  arr[val as PageType] = true;
  return arr;
}, {} as Record<PageType, boolean>);

export const PageTypeAndDepthIcon = z.function().args(PageType, z.number()).returns(z.string()).implement((pageType, depth) => {
  switch (pageType) {
    case PageType.enum.Folder:
      switch (depth) {
        case 0:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_ELEARNING;
        case 1:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_PIPE;
        default:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_FOLDER;
      }
    case PageType.enum.Slide:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_SLIDE;
    case PageType.enum.Grid:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_GRID;
    case PageType.enum.Case:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_CASE;
    case PageType.enum.Training:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_TRAINING;
    case PageType.enum.QuizStart:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_QUIZSTART;
    case PageType.enum.Vocabulary:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_VOCABULARY;
    default:
      return FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_FOLDER;
  }
});

export const CreatePageTypeAndDepthIcon = z.function().args(PageType, z.number()).returns(z.string()).implement((pageType, depth) => {
  const { PAGE_PIPE, PAGE_FOLDER, PAGE_GRID, PAGE_SLIDE} = FlatModels.ButtonDesignModels.ButtonDefaultIcons;
  switch (pageType) {
    case PageType.enum.Folder: {
      switch (depth) {
        case 0:
          return PAGE_PIPE;
        case 1:
          return PAGE_FOLDER;
        default:
          return PAGE_GRID;
      }
    }
    default:
      return PAGE_SLIDE;
    }
});


export const PageTypeIcon: Record<string, string> = {
  [PageType.enum.Folder]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_FOLDER,
  [PageType.enum.Grid]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_GRID,
  [PageType.enum.Case]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_CASE,
  [PageType.enum.Training]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_TRAINING,
  [PageType.enum.QuizStart]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_QUIZSTART,
  [PageType.enum.Vocabulary]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_VOCABULARY,
}

export const SlideStatus = z.enum([
  'empty',
  'draft',
  'in_review',
  'review_not_ok',
  'final',
]);
export type SlideStatus = z.infer<typeof SlideStatus>;

export const DefaultsSchema = z.object({
  labelColor: z.string().max(255).nullable().default(null),
  pointerColor: z.string().max(255).nullable().default(null),
  labelFontSize: z.number().nullable().default(null),
});

export type Defaults = z.infer<typeof DefaultsSchema>;

export const PageSchema = BaseSchema.extend({
  title: z.string().min(1).max(255),
  type: PageType,
  editedBy: z.string().uuid().nullable().optional(),
  editedAt: z.coerce.date().optional(),
  authorNote: z.string().nullable().optional(),
  usedMedias: z.array(z.string().uuid()).optional()
});

export type Page = z.infer<typeof PageSchema>;

export const FolderSchema = PageSchema.extend({
  type: z.literal('Folder'),
  description: z.string().nullable().default(null),
  body: z.string().nullable().default(null),
  media: z.string().uuid().nullable().optional(),
  themeColor: z.string().max(255).nullable().default(null),
});

export type Folder = z.infer<typeof FolderSchema>;

export const GridSchema = PageSchema.extend({
  type: z.literal('Grid'),
  description: z.string().nullable().default(null),
  body: z.string().nullable().default(null),
  pdfMedia: z.string().uuid().nullable().optional(),
  pdfMediaAuthor: z.string().uuid().nullable().optional(),
});

export type Grid = z.infer<typeof GridSchema>;

export const CaseSchema = GridSchema.extend({
  type: z.literal('Case'),
});

export type Case = z.infer<typeof CaseSchema>;

export const SlideSchema = PageSchema.extend({
  type: z.literal('Slide'),
  slideId: z.number().nullable().default(null),
  header: z.string().max(255).nullable().default(null),
  description: z.string().nullable().default(null),
  body: z.string().nullable().default(null),
  status: SlideStatus.default(SlideStatus.enum.empty),
  mediaType: FlatModels.MediaEntityModels.MediaType.nullable().default(null),
  modality: z.string().uuid().optional().nullable(),
  sideBarSize: z.number().nullable().default(3),
  groups: z.array(z.string().uuid()).default([]),
  thumbnailMedia: z.string().uuid().optional().nullable(),
  pdfMedia: z.string().uuid().optional().nullable(),
  pdfMediaAuthor: z.string().uuid().optional().nullable(),
  layers: z.array(z.string().uuid()).default([]),
  annotations: z.array(z.string().uuid()).default([]),
  pois: z.array(z.string().uuid()).default([]),
  questions: z.array(z.string().uuid()).default([]),
  defaults: DefaultsSchema,
  createdAt: z.coerce.date().optional(),
});

export type Slide = z.infer<typeof SlideSchema>;

export const TrainingSchema = SlideSchema.extend({
  type: z.literal('Training'),
  url: z.string().optional(),
  answeredRight: z.boolean().optional(),
  answered: z.boolean().optional(),
  toggleSolution: z.number().optional(),
});

export type Training = z.infer<typeof TrainingSchema>;

export const QuizStartSchema = PageSchema.extend({
  type: z.literal('QuizStart'),
  body: z.string().nullable().default(null),
});

export type QuizStart = z.infer<typeof QuizStartSchema>;

export const QuizRunSchema = PageSchema.extend({
  type: z.literal('QuizRun'),
});

export type QuizRun = z.infer<typeof QuizRunSchema>;

export const VocabularySchema = PageSchema.extend({
  type: z.literal('Vocabulary'),
  body: z.string().nullable().default(null),
  vocabularyGroups: z.array(z.string().uuid()).default([]),
});

export type Vocabulary = z.infer<typeof VocabularySchema>;

export const InfoPageSchema = PageSchema.extend({
  type: z.literal('InfoPage'),
  body: z.string().nullable().default(null),
  header: z.string().max(255).nullable().default(null),
});

export type InfoPage = z.infer<typeof InfoPageSchema>;

export const PageEntityTypeSchema = z.discriminatedUnion('type', [
  FolderSchema,
  GridSchema,
  CaseSchema,
  SlideSchema,
  TrainingSchema,
  QuizStartSchema,
  QuizRunSchema,
  VocabularySchema,
  InfoPageSchema
]);

export type PageEntityType = z.infer<typeof PageEntityTypeSchema>;

export const BodyPageEntityTypeSchema = z.discriminatedUnion('type', [
  FolderSchema,
  GridSchema,
  SlideSchema,
  TrainingSchema,
  QuizStartSchema,
  VocabularySchema,
  InfoPageSchema
]);

export type BodyPageEntityType = z.infer<typeof BodyPageEntityTypeSchema>;

export const DescriptionPageEntityTypeSchema = z.discriminatedUnion('type', [
  FolderSchema,
  GridSchema,
  SlideSchema,
  TrainingSchema,
]);

export type DescriptionPageEntityType = z.infer<typeof DescriptionPageEntityTypeSchema>;

export const PageEntityQueryResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: PageEntityTypeSchema,
});

export type PageEntityQueryResult = z.infer<typeof PageEntityQueryResultSchema>;

